import { Alert } from '@synoptic/ui-kit/alert/alert.js';
import { Button } from '@synoptic/ui-kit/button/button.js';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@synoptic/ui-kit/collapsible/collapsible.tsx';
import { SoundOffIcon } from '@synoptic/ui-kit/icons/react/sound-off.tsx';
import {
  ModalContent,
  ModalHeader,
  ModalRoot,
  ModalTitle,
} from '@synoptic/ui-kit/modal/modal.js';
import {
  Select,
  SelectContent,
  SelectTrigger,
} from '@synoptic/ui-kit/select/select.js';
import { Separator } from '@synoptic/ui-kit/separator/separator.js';
import { Slider } from '@synoptic/ui-kit/slider/slider.js';
import { Switch } from '@synoptic/ui-kit/switch/switch.tsx';
import { FC, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { CustomCollection } from '@/__generated__/schema-types.ts';
import {
  SoundTrack,
  DEFAULT_SOUND_TRACK,
  DEFAULT_SOUND_VOLUME,
  CollectionSoundSettings,
  SOUND_TRACK_LABELS,
  isReadAloudTrack,
} from '@/components/toggle-sound/sound-settings';
import { SoundStatus } from '@/components/toggle-sound/sound-status.ts';
import { playNotificationAudio } from '@/utils/audio-utils/play-notification-audio';
import { isReadAloudSupported } from '@/utils/audio-utils/read-aloud.ts';
import {
  settingsContainer,
  settingGroup,
  settingLabel,
  switchGroup,
  switchLabel,
  highlightSoundSection,
  expandableSwitchGroupLabel,
} from './sound-settings-modal.css';

export type BaseSoundSettings = {
  track: SoundTrack;
  volume: number;
};

export type SoundSettingsModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  collection?: CustomCollection;
  settings?: BaseSoundSettings;
  soundStatus?: SoundStatus;
  showHighlightSettings?: boolean;
  onToggleSound?: () => void;
  onTrackChange?: (track: SoundTrack) => void;
  onVolumeChange?: (volume: number) => void;
  onHighlightSoundEnabledChange?: (enabled: boolean) => void;
  onHighlightTrackChange?: (track: SoundTrack) => void;
  onHighlightVolumeChange?: (volume: number) => void;
  onOnlyHighlightsChange?: (onlyHighlights: boolean) => void;
};

export const SoundSettingsModal: FC<SoundSettingsModalProps> = ({
  open,
  onOpenChange,
  collection,
  settings,
  soundStatus,
  showHighlightSettings = true,
  onToggleSound,
  onTrackChange,
  onVolumeChange,
  onHighlightSoundEnabledChange,
  onHighlightTrackChange,
  onHighlightVolumeChange,
  onOnlyHighlightsChange,
}) => {
  const currentTrack = settings?.track || DEFAULT_SOUND_TRACK;
  const currentVolume = settings?.volume ?? DEFAULT_SOUND_VOLUME;
  const highlightSoundEnabled =
    'highlightSound' in (settings || {})
      ? ((settings as CollectionSoundSettings).highlightSound?.enabled ?? false)
      : false;
  const highlightTrack =
    'highlightSound' in (settings || {})
      ? (settings as CollectionSoundSettings).highlightSound?.track ||
        SoundTrack.FingerSnap
      : SoundTrack.FingerSnap;
  const highlightVolume =
    'highlightSound' in (settings || {})
      ? ((settings as CollectionSoundSettings).highlightSound?.volume ??
        DEFAULT_SOUND_VOLUME)
      : DEFAULT_SOUND_VOLUME;
  const onlyHighlights =
    'onlyHighlights' in (settings || {})
      ? ((settings as CollectionSoundSettings).onlyHighlights ?? false)
      : false;

  const hasHighlights =
    showHighlightSettings &&
    collection?.highlights?.isEnabled &&
    collection?.highlights?.values &&
    collection.highlights.values.length > 0;

  const isSoundOff = soundStatus === SoundStatus.off;

  const [localVolume, setLocalVolume] = useState(currentVolume);
  const [localHighlightVolume, setLocalHighlightVolume] =
    useState(highlightVolume);

  const debouncedVolumeChange = useDebouncedCallback((volume: number) => {
    onVolumeChange?.(volume);
  }, 200);

  const debouncedHighlightVolumeChange = useDebouncedCallback(
    (volume: number) => {
      onHighlightVolumeChange?.(volume);
    },
    200,
  );

  const handleVolumeChange = (values: number[]) => {
    const newVolume = values[0];
    setLocalVolume(newVolume);
    debouncedVolumeChange(newVolume);
  };

  const handleHighlightVolumeChange = (values: number[]) => {
    const newVolume = values[0];
    setLocalHighlightVolume(newVolume);
    debouncedHighlightVolumeChange(newVolume);
  };

  const soundTrackItems = Object.entries(SOUND_TRACK_LABELS)
    .filter(
      ([value]) =>
        !isReadAloudTrack(value as SoundTrack) || isReadAloudSupported(),
    )
    .map(([value, label]) => ({ value, content: label }));

  const playPreview = async (track: SoundTrack, volume: number) => {
    try {
      await playNotificationAudio({
        track,
        volume,
        text: 'New posts will be read aloud like this.',
        ownerId: 'sound-settings-preview',
        interruptSpeech: true,
      });
    } catch {
      // ignore
    }
  };

  const createTrackChangeHandler =
    (
      onTrackChange: ((track: SoundTrack) => void) | undefined,
      volume: number,
    ) =>
    (track: SoundTrack) => {
      onTrackChange?.(track);
      void playPreview(track, volume);
    };

  const handleTrackChange = createTrackChangeHandler(
    onTrackChange,
    localVolume,
  );
  const handleHighlightTrackChange = createTrackChangeHandler(
    onHighlightTrackChange,
    localHighlightVolume,
  );

  const handleVolumeCommit = ([volume]: number[]) => {
    void playPreview(currentTrack, volume);
  };

  const handleHighlightVolumeCommit = ([volume]: number[]) => {
    void playPreview(highlightTrack, volume);
  };

  return (
    <ModalRoot open={open} onOpenChange={onOpenChange}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Sound settings</ModalTitle>
        </ModalHeader>

        <div className={settingsContainer}>
          {isSoundOff && (
            <Alert
              icon={<SoundOffIcon />}
              primaryAction={
                <Button
                  size="small"
                  variant="secondary"
                  onClick={onToggleSound}
                >
                  Enable sound
                </Button>
              }
            >
              Sound is currently off{collection ? ' for this collection' : ''}.
            </Alert>
          )}

          <div className={settingGroup}>
            <label className={settingLabel}>Sound track</label>
            <Select
              value={currentTrack}
              onValueChange={(value) => handleTrackChange(value as SoundTrack)}
            >
              <SelectTrigger
                triggerText={SOUND_TRACK_LABELS[currentTrack]}
                placeholder="Select sound"
              />
              <SelectContent items={soundTrackItems} />
            </Select>
          </div>

          <div className={settingGroup}>
            <label className={settingLabel}>
              Volume: {Math.round(localVolume * 100)}%
            </label>
            <Slider
              value={[localVolume]}
              onValueChange={handleVolumeChange}
              onValueCommit={handleVolumeCommit}
              min={0}
              max={1}
              step={0.01}
              withLabels={false}
            />
          </div>

          {hasHighlights && (
            <>
              <Separator />

              <Collapsible open={highlightSoundEnabled}>
                <CollapsibleTrigger asChild>
                  <label className={expandableSwitchGroupLabel}>
                    Enable different sound for highlights
                    <Switch
                      checked={highlightSoundEnabled}
                      onCheckedChange={onHighlightSoundEnabledChange}
                    />
                  </label>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className={highlightSoundSection}>
                    <div className={settingGroup}>
                      <label className={settingLabel}>
                        Highlight sound track
                      </label>
                      <Select
                        value={highlightTrack}
                        onValueChange={(value) =>
                          handleHighlightTrackChange(value as SoundTrack)
                        }
                      >
                        <SelectTrigger
                          triggerText={SOUND_TRACK_LABELS[highlightTrack]}
                          placeholder="Select sound"
                        />
                        <SelectContent items={soundTrackItems} />
                      </Select>
                    </div>

                    <div className={settingGroup}>
                      <label className={settingLabel}>
                        Volume: {Math.round(localHighlightVolume * 100)}%
                      </label>
                      <Slider
                        value={[localHighlightVolume]}
                        onValueChange={handleHighlightVolumeChange}
                        onValueCommit={handleHighlightVolumeCommit}
                        min={0}
                        max={1}
                        step={0.01}
                        withLabels={false}
                      />
                    </div>

                    <div className={switchGroup}>
                      <label className={switchLabel}>
                        Keep sound only for highlights
                      </label>
                      <Switch
                        checked={onlyHighlights}
                        onCheckedChange={onOnlyHighlightsChange}
                      />
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </>
          )}
        </div>
      </ModalContent>
    </ModalRoot>
  );
};
